import React from 'react'

import { getVersionRenderStatus } from '@sketch/modules-common'
import { VersionFragment, useGetSectionsForPageQuery } from '@sketch/gql-types'

import { ShareWithoutVersion } from '../../../../versioning'
import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import { Panel } from '../../../components/Panel'
import { FloatingZoom } from '../../../FrameView/components/FloatingZoom'
import { CanvasSurface } from '../../../components/CanvasSurface'
import {
  CanvasMobileToolbar,
  FOCUS_VIEW_TOOLBAR_ITEMS,
} from '../../../components/CanvasMobileToolbar'
import { CanvasLeftPanel } from '../../../../canvas/components/CanvasLeftPanel'
import ShareReadyGuard from '../../../components/ShareReadyGuard'
import { SectionSelector } from '../SectionSelector'
import { SectionDetailHeader } from '../SectionDetailHeader'

interface SectionViewProps {
  layoutProps: DocumentSidebarLayoutExtraProps
  share: ShareWithoutVersion
  currentVersion: VersionFragment | null
  pageUUID: string
  sectionUUID: string
  isViewingLatestVersion: boolean
  renderStatus: ReturnType<typeof getVersionRenderStatus>
}

/**
 * Layout for the single-Section focus view - the Section equivalent of
 * FrameView. The canvas itself is the shared CanvasSurface, which isolates the
 * Section by making it the renderer's scene root; only the header and the
 * panel's navigation differ from the plain page view.
 */
export const SectionView = ({
  layoutProps,
  share,
  currentVersion,
  pageUUID,
  sectionUUID,
  isViewingLatestVersion,
  renderStatus,
}: SectionViewProps) => {
  const {
    HeaderPortal,
    SidebarRightPortal,
    toggleSidebarRight,
    isSidebarRightOpen,
  } = layoutProps

  // GQL-backed, so this resolves independently of the WASM engine parsing
  // the presentation file - the header can populate as soon as the network
  // responds, instead of waiting on usePRFileSections().
  const { data } = useGetSectionsForPageQuery({
    variables: {
      identifier: share.identifier,
      versionShortId: currentVersion?.shortId ?? '',
      pageUUID,
    },
    skip: !currentVersion?.shortId,
  })
  const page = data?.share?.version?.document?.page
  const currentSection = page?.sections.find(
    section => section.uuid === sectionUUID
  )

  // Always defined from data we already have synchronously (sectionUUID and
  // pageUUID both come from the URL) so the header renders immediately on
  // navigation instead of staying empty - name/pageName fill in once the
  // GQL query resolves, in place, without remounting anything.
  const sectionInfo = {
    uuid: sectionUUID,
    name: currentSection?.name ?? '',
    pageUUID,
    pageName: page?.name ?? '',
  }

  return (
    <>
      <HeaderPortal>
        <SectionDetailHeader
          share={share}
          sectionInfo={sectionInfo}
          toggleSidebarRight={toggleSidebarRight}
          isSidebarRightOpen={isSidebarRightOpen}
          isViewingLatestVersion={isViewingLatestVersion}
          userCanOpenInApp={!!currentVersion?.document?.userCanOpenInApp}
          currentVersion={currentVersion}
        />

        <Panel
          share={share}
          version={currentVersion || undefined}
          userCanSeeComments
          userCanInspect={share.userCanInspect}
          SidebarRightPortal={SidebarRightPortal}
        />

        <FloatingZoom />
      </HeaderPortal>

      <ShareReadyGuard renderStatus={renderStatus}>
        <CanvasSurface>
          <CanvasLeftPanel>
            <SectionSelector />
          </CanvasLeftPanel>
        </CanvasSurface>
        <CanvasMobileToolbar items={FOCUS_VIEW_TOOLBAR_ITEMS} />
      </ShareReadyGuard>
    </>
  )
}
